import React from "react";
import { Link } from "react-router-dom";
import { useCatalog } from "@/context/CatalogContext";
import { useStock } from "@/context/StockContext";
import { useResearchIndex } from "@/hooks/useResearchIndex";
import { ProductCard } from "@/components/ProductGrid";
import { ArrowRight, BookOpen, FileText, ShieldCheck } from "lucide-react";

const TRUST = ["Reference-Grade", "Lyophilized", "Sealed Vials", "Controlled Handling", "Research Use Only"];

export const TrustLine = () => (
  <div className="border-y border-zinc-900 bg-[#0C0C0C] py-5" data-testid="trust-line">
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
      {TRUST.map((w, i) => (
        <span key={w} className="flex items-center gap-3 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]/80">
          {w}{i < TRUST.length - 1 && <span className="text-[#27272A]">✦</span>}
        </span>
      ))}
    </div>
  </div>
);

const CATS = [
  { label: "Metabolic", cat: "Metabolic" },
  { label: "Growth & Recovery", cat: "Growth" },
  { label: "Longevity & Cellular", cat: "Longevity" },
  { label: "Specialty Research", cat: "Specialty" },
];

export const ShopByCategory = () => (
  <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-20" data-testid="shop-by-category">
    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-6">Shop by Category</p>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
      {CATS.map((c) => (
        <Link key={c.cat} to={`/catalog?cat=${encodeURIComponent(c.cat)}`} data-testid={`category-${c.cat.toLowerCase()}`}
          className="group bg-[#0A0A0A] hover:bg-[#101010] p-6 md:p-8 flex items-center justify-between transition-colors">
          <span className="font-serif text-lg md:text-2xl font-light leading-tight group-hover:text-[#D4AF37] transition-colors">{c.label}</span>
          <ArrowRight size={16} className="text-[#52525B] group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all shrink-0" />
        </Link>
      ))}
    </div>
  </section>
);

export const FeaturedProducts = () => {
  const { products } = useCatalog();
  const { isSoldOut } = useStock();
  const { byProduct } = useResearchIndex();
  const featured = products.filter((p) => !isSoldOut(p)).sort((a, b) => (b.bestseller ? 1 : 0) - (a.bestseller ? 1 : 0)).slice(0, 6);
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="featured-products">
      <div className="flex items-end justify-between gap-6 mb-8">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Featured Research Compounds</p>
          <h2 className="font-serif text-3xl md:text-5xl font-light leading-none">The Catalog, Curated.</h2>
        </div>
        <Link to="/catalog" data-testid="view-full-catalog-btn"
          className="hidden sm:inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors shrink-0">
          View Full Catalog <ArrowRight size={13} />
        </Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-px">
        {featured.map((p, i) => <ProductCard key={p.id} p={p} index={i} verified={!!byProduct[p.id]?.identityVerified} featured={p.bestseller} />)}
      </div>
      <Link to="/catalog" data-testid="view-full-catalog-mobile"
        className="sm:hidden mt-8 flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-6 py-4 font-mono text-[10px] uppercase tracking-[0.2em]">
        View Full Catalog <ArrowRight size={13} />
      </Link>
    </section>
  );
};

const TRIO = [
  { Icon: BookOpen, to: "/library", title: "Research Library", sub: "Explore compounds and research information.", tid: "home-library" },
  { Icon: FileText, to: "/documentation", title: "Documentation", sub: "Specifications and available documentation.", tid: "home-docs" },
  { Icon: ShieldCheck, to: "/verify", title: "Batch Verification", sub: "Verify a Molecularix batch or lot number.", tid: "home-verify" },
];

export const ResearchTrio = () => (
  <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24" data-testid="research-trio">
    <div className="grid sm:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
      {TRIO.map(({ Icon, to, title, sub, tid }) => (
        <Link key={to} to={to} data-testid={tid} className="group bg-[#0A0A0A] hover:bg-[#101010] p-6 md:p-8 transition-colors">
          <Icon size={19} className="text-[#D4AF37] mb-4" />
          <p className="font-serif text-xl font-light mb-1 group-hover:text-[#D4AF37] transition-colors flex items-center gap-2">{title} <ArrowRight size={14} className="text-[#52525B] group-hover:translate-x-1 transition-transform" /></p>
          <p className="text-[#71717A] text-sm leading-relaxed">{sub}</p>
        </Link>
      ))}
    </div>
  </section>
);
